import {ref} from "vue";
import {defineStore} from "pinia";
import axios from "axios";
import type {AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from "axios";
import type {ImpersonateCallbackResponse, LoginCallbackResponse} from "@/types/auth";
import {useApiStore} from "@/stores/api";
import type {LoggedInUserData} from "@/types/LoggedInuser";
import {LoggedInUser} from "@/types/LoggedInuser";
import {AccountPermissions} from "@/types/permissions/AccountPermission";
import type {MaintenanceInfo} from "@/types/MaintenanceInfo";
import {useRouter} from 'vue-router';
import {clearKeyPair, clearNonce, getNonce, makeDpopProof, setNonce} from "@/auth/dpop";
import * as Sentry from "@sentry/vue";
import Swal from "sweetalert2";

const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL ?? '';
const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const DEFAULT_TOKEN_LIFETIME_MS = 15 * 60_000;
const PRE_REQUEST_REFRESH_THRESHOLD_MS = 30_000;
const REFRESH_TIMEOUT_MS = 20_000;
const MAX_NONCE_RETRIES = 3;
const IMPERSONATION_WARNING_MS = 5 * 60_000;

// Lifetime as a duration (exp - iat) instead of the absolute exp claim — comparing a
// server-issued exp against the local clock would inherit the client's clock offset.
const tokenLifetimeMs = (jwt: string): number => {
    try {
        const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const lifetime = (payload.exp - payload.iat) * 1000;
        return Number.isFinite(lifetime) && lifetime > 0 ? lifetime : DEFAULT_TOKEN_LIFETIME_MS;
    } catch {
        return DEFAULT_TOKEN_LIFETIME_MS;
    }
};

const tokenIssuedAtMs = (jwt: string): number => {
    try {
        const parts = jwt.split('.');
        const payloadPart = parts[1];
        const payload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
        const issuedAt = payload.iat * 1000;
        if (!Number.isFinite(issuedAt)) {
            return Date.now();
        }
        if (issuedAt <= 0) {
            return Date.now();
        }
        return issuedAt;
    } catch {
        return Date.now();
    }
};

const isTokenExpired = (jwt: string): boolean => {
    const expiresAt = tokenIssuedAtMs(jwt) + tokenLifetimeMs(jwt);
    if (expiresAt <= Date.now()) {
        return true;
    }
    return false;
};

type RetryableConfig = InternalAxiosRequestConfig & {
    _dpopNonceRetries?: number;
    _refreshRetry?: boolean;
};

// Fire-and-forget diagnostics into the backend's client_login log — production users'
// browser consoles are unreachable, so session failures must leave a server-side trace.
// Bare axios: the intercepted instances would try to attach auth to a dying session.
const logSessionEvent = (event: string, context: Record<string, unknown>): void => {
    if (!BASE_URL) return;
    try {
        axios.post(`${BASE_URL}/api/loginEvent`, {event, context}).catch(() => {});
    } catch {}
};

type RefreshOutcome = 'ok' | 'transient' | 'definitive';

export type SessionMessage =
    | { type: 'activity' }
    | { type: 'logout' }
    | { type: 'token_updated'; token: string; ts: number };

let sessionChannel: BroadcastChannel | null = null;
try { sessionChannel = new BroadcastChannel('oneclick-session'); }
catch { sessionChannel = null; }

export function sendSessionMessage(msg: SessionMessage): void {
    try { sessionChannel?.postMessage(msg); }
    catch {}
}

export function onSessionMessage(handler: (msg: SessionMessage) => void): () => void {
    if (!sessionChannel) return () => {};
    const wrapped = (event: MessageEvent<SessionMessage>) => handler(event.data);
    sessionChannel.addEventListener('message', wrapped);
    return () => sessionChannel?.removeEventListener('message', wrapped);
}

export const useAuthStore = defineStore("auth", () => {
    const token = ref<string>("");
    const impersonating = ref(false);
    const isLoggedIn = ref(false);
    const api = useApiStore();
    const user = ref<LoggedInUser|null>(null);
    const accountPermissions = ref<AccountPermissions>(new AccountPermissions());
    const hiddenMaintenanceNotificationId = ref<number|null>(null);
    const maintenanceInfo = ref<MaintenanceInfo|null>(null);
    const hiddenClientSiteNotificationIds = ref<Array<number>>([]);
    const router = useRouter();

    let lastTokenSetAt = 0;
    let tokenExpiresAt = 0;
    let refreshInFlight: Promise<RefreshOutcome> | null = null;
    let impersonationWarningTimer = 0;
    let impersonationLogoutTimer = 0;

    const clearImpersonationTimers = () => {
        window.clearTimeout(impersonationWarningTimer);
        window.clearTimeout(impersonationLogoutTimer);
    };

    const trackTokenExpiry = (jwt: string, issuedAtMs: number) => {
        tokenExpiresAt = issuedAtMs + tokenLifetimeMs(jwt);
    };

    const ensureFreshToken = async (minRemainingMs: number = PRE_REQUEST_REFRESH_THRESHOLD_MS): Promise<void> => {
        if (impersonating.value) {
            return;
        }
        if (!token.value || tokenExpiresAt === 0) return;
        if (Date.now() < tokenExpiresAt - minRemainingMs) return;
        await refreshAccessToken();
    };

    onSessionMessage((msg) => {
        if (msg.type === 'token_updated' && typeof msg.token === 'string' && msg.token !== '') {
            token.value = msg.token;
            lastTokenSetAt = msg.ts ?? Date.now();
            isLoggedIn.value = true;
            trackTokenExpiry(msg.token, lastTokenSetAt);
        }
    });

    const closeMaintenanceNotificationId = (hidden_maintenance_notification_id:number) => {
        hiddenMaintenanceNotificationId.value = hidden_maintenance_notification_id;
        localStorage.setItem('hidden_maintenance_notification_id', hidden_maintenance_notification_id.toString());
    }

    const getClosedMaintenanceNotificationId = () => {
        const notification_id = localStorage.getItem('hidden_maintenance_notification_id');
        hiddenMaintenanceNotificationId.value = notification_id === null ? null : parseInt(notification_id);
    }

    const deleteClosedMaintenanceNotificationId = () => {
        localStorage.removeItem('hidden_maintenance_notification_id');
    }

    const closeSiteNotificationId = (hidden_site_notification_id:number) => {
        hiddenClientSiteNotificationIds.value.push(hidden_site_notification_id);
        localStorage.setItem('hidden_client_site_notification_ids', JSON.stringify(hiddenClientSiteNotificationIds.value));
    }

    const getClosedSiteNotificationIds = () => {
        const notification_ids = localStorage.getItem('hidden_client_site_notification_ids');
        hiddenClientSiteNotificationIds.value = notification_ids === null ? [] : JSON.parse(notification_ids);
    }

    const deleteClosedSiteNotificationIds = () => {
        localStorage.removeItem('hidden_client_site_notification_ids');
    }

    const attachDpopRequest = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        if (!config.headers) return config;

        const cfg = config as RetryableConfig;
        const isRetry = (cfg._dpopNonceRetries ?? 0) > 0 || !!cfg._refreshRetry;

        if ((!config.headers['DPoP'] || isRetry) && token.value) {
            await ensureFreshToken();
            const tokenSnapshot = token.value;
            if (tokenSnapshot) {
                const proof = await makeDpopProof({
                    htu: combineUrl(config.baseURL, config.url),
                    htm: (config.method ?? 'get').toUpperCase(),
                    accessToken: tokenSnapshot,
                    nonce: getNonce() ?? undefined,
                });
                config.headers['Authorization'] = `DPoP ${tokenSnapshot}`;
                config.headers['DPoP'] = proof;
            }
        }
        return config;
    };

    const combineUrl = (baseURL: string | undefined, requestUrl: string | undefined): string => {
        const url = requestUrl ?? '';
        if (/^https?:\/\//i.test(url)) return url.split('?')[0].split('#')[0];
        const base = (baseURL ?? window.location.origin).replace(/\/+$/, '');
        const path = url.replace(/^\/+/, '');
        return (base + '/' + path).split('?')[0].split('#')[0];
    };

    const captureNonceFromResponse = (response: AxiosResponse): AxiosResponse => {
        const nonce = response.headers?.['dpop-nonce'];
        if (typeof nonce === 'string') setNonce(nonce);
        return response;
    };

    const makeErrorHandler = (instance: AxiosInstance) => async (error: AxiosError): Promise<unknown> => {
        const response = error.response;
        const config = error.config as RetryableConfig | undefined;
        if (!config || !response) return Promise.reject(error);

        const headerNonce = response.headers?.['dpop-nonce'];
        if (typeof headerNonce === 'string') setNonce(headerNonce);

        const data = response.data as { error?: string } | undefined;

        if (response.status === 401 && data?.error === 'use_dpop_nonce' && (config._dpopNonceRetries ?? 0) < MAX_NONCE_RETRIES) {
            config._dpopNonceRetries = (config._dpopNonceRetries ?? 0) + 1;
            return instance.request(config);
        }

        if (response.status === 401 && impersonating.value) {
            await localLogout();
            return Promise.reject(error);
        }

        if (
            response.status === 401
            && !config._refreshRetry
            && token.value
            && data?.error !== 'invalid_dpop_proof'
            && data?.error !== 'use_dpop_nonce'
        ) {
            config._refreshRetry = true;
            const outcome = await runRefresh();
            if (outcome === 'ok') return instance.request(config);
            if (outcome === 'definitive') {
                logSessionEvent('forced_logout', {
                    request_url: config.url,
                    request_method: config.method,
                    request_status: response.status,
                    request_error: data?.error,
                });
                sendSessionMessage({ type: 'logout' });
                await localLogout();
            }
        }

        return Promise.reject(error);
    };

    api.axiosApi.interceptors.request.use(attachDpopRequest);
    api.axiosAuth.interceptors.request.use(attachDpopRequest);
    api.axiosApi.interceptors.response.use(captureNonceFromResponse, makeErrorHandler(api.axiosApi));
    api.axiosAuth.interceptors.response.use(captureNonceFromResponse, makeErrorHandler(api.axiosAuth));

    const doRefresh = async (): Promise<RefreshOutcome> => {
        if (token.value && Date.now() - lastTokenSetAt < 5000) {
            isLoggedIn.value = true;
            return 'ok';
        }
        try {
            const proof = await makeDpopProof({
                htu: `${AUTH_BASE_URL}/oauth/token`,
                htm: 'POST',
                nonce: getNonce() ?? undefined,
            });
            const response = await axios.post(`${BASE_URL}/api/refresh`, null, {
                withCredentials: true,
                headers: {DPoP: proof},
                timeout: REFRESH_TIMEOUT_MS,
            });
            const newNonce = response.headers?.['dpop-nonce'];
            if (typeof newNonce === 'string') setNonce(newNonce);

            const newToken: string = response.data?.access_token ?? '';
            token.value = newToken;
            isLoggedIn.value = !!newToken;
            if (newToken) {
                lastTokenSetAt = Date.now();
                trackTokenExpiry(newToken, lastTokenSetAt);
                sendSessionMessage({ type: 'token_updated', token: newToken, ts: lastTokenSetAt });
                return 'ok';
            }
            return 'transient';
        } catch (e) {
            const err = e as AxiosError;
            const newNonce = err.response?.headers?.['dpop-nonce'];
            if (typeof newNonce === 'string') {
                setNonce(newNonce);
                if (err.response?.status === 401 && (err.response.data as {error?: string})?.error === 'use_dpop_nonce') {
                    return doRefresh();
                }
            }
            const status = err.response?.status;
            const errorCode = (err.response?.data as {error?: string} | undefined)?.error;
            if ((status === 400 || status === 401) && errorCode !== 'use_dpop_nonce' && errorCode !== 'missing_refresh_cookie') {
                console.error('session refresh rejected definitively', status, err.response?.data);
                logSessionEvent('refresh_rejected', {
                    status,
                    error: errorCode,
                    body: JSON.stringify(err.response?.data ?? null).slice(0, 500),
                });
                return 'definitive';
            }
            return 'transient';
        }
    };

    const runRefresh = async (): Promise<RefreshOutcome> => {
        if (refreshInFlight) return refreshInFlight;
        refreshInFlight = (async () => {
            try {
                if (typeof navigator?.locks?.request === 'function') {
                    return await navigator.locks.request('dpop-refresh', doRefresh);
                }
                return await doRefresh();
            } finally {
                refreshInFlight = null;
            }
        })();
        return refreshInFlight;
    };

    const refreshAccessToken = async (): Promise<boolean> => {
        if (impersonating.value) {
            return false;
        }
        if (!BASE_URL || !AUTH_BASE_URL) {
            console.warn('Cannot refresh: VITE_BASE_URL or VITE_AUTH_BASE_URL is not set');
            return false;
        }
        return (await runRefresh()) === 'ok';
    };

    const initApp = async (options: { skipRefresh?: boolean, skipImpersonation?: boolean } = {}) => {
        getClosedMaintenanceNotificationId();
        getClosedSiteNotificationIds();
        let impersonationToken = localStorage.getItem('impersonation_token');
        if (impersonationToken) {
            let mustDiscardToken = false;
            if (options.skipImpersonation) {
                mustDiscardToken = true;
            }
            if (isTokenExpired(impersonationToken)) {
                mustDiscardToken = true;
            }
            if (mustDiscardToken) {
                localStorage.removeItem('impersonation_token');
                impersonationToken = null;
            }
        }
        if (impersonationToken) {
            startImpersonation(impersonationToken);
        } else if (!options.skipRefresh && localStorage.getItem('has_session') === '1') {
            const outcome = await runRefresh();
            if (outcome === 'definitive') localStorage.removeItem('has_session');
        }
        accountPermissions.value.permissionList = [];
    };

    const loginUser = (user_data: LoginCallbackResponse) => {
        token.value = user_data.access_token;
        impersonating.value = false;
        localStorage.removeItem('impersonation_token');
        isLoggedIn.value = !!token.value;
        if (token.value) {
            localStorage.setItem('has_session', '1');
            lastTokenSetAt = Date.now();
            trackTokenExpiry(token.value, lastTokenSetAt);
        }
        localStorage.removeItem('last_activity_at');
        deleteClosedMaintenanceNotificationId();
        deleteClosedSiteNotificationIds();
        accountPermissions.value.permissionList = [];
    };

    const serverNow = async (): Promise<number> => {
        try {
            const response = await fetch(`${AUTH_BASE_URL}/api/time`, {cache: 'no-store'});
            const body = await response.json();
            const now = Number(body.now) * 1000;
            if (!response.ok) {
                return Date.now();
            }
            if (!Number.isFinite(now)) {
                return Date.now();
            }
            return now;
        } catch {
            return Date.now();
        }
    };

    const startImpersonation = async (accessToken: string) => {
        let hasToken = false;
        if (accessToken) {
            hasToken = true;
        }

        token.value = accessToken;
        impersonating.value = hasToken;
        isLoggedIn.value = hasToken;
        lastTokenSetAt = Date.now();

        const serverTime = await serverNow();
        const clockOffset = Date.now() - serverTime;
        const expiresAtOnServer = tokenIssuedAtMs(accessToken) + tokenLifetimeMs(accessToken);
        tokenExpiresAt = expiresAtOnServer + clockOffset;

        clearImpersonationTimers();

        let warningDelay = tokenExpiresAt - Date.now() - IMPERSONATION_WARNING_MS;
        if (warningDelay < 0) {
            warningDelay = 0;
        }

        let logoutDelay = tokenExpiresAt - Date.now();
        if (logoutDelay < 0) {
            logoutDelay = 0;
        }

        impersonationWarningTimer = window.setTimeout(showImpersonationWarning, warningDelay);
        impersonationLogoutTimer = window.setTimeout(() => {
            if (impersonating.value) {
                localLogout();
            }
        }, logoutDelay);
    };

    const formatTimeLeft = (expiresAt: number): string => {
        let seconds = Math.round((expiresAt - Date.now()) / 1000);
        if (seconds < 0) {
            seconds = 0;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = String(seconds % 60).padStart(2, '0');
        return `${minutes}:${remainingSeconds}`;
    };

    const showImpersonationWarning = () => {
        if (!impersonating.value) {
            return;
        }

        const expiresAt = tokenExpiresAt;
        let countdownInterval = 0;

        Swal.fire({
            title: 'Impersonation ending',
            html: `Your impersonation expires in <b>${formatTimeLeft(expiresAt)}</b>. If you need more time, impersonate the customer again.`,
            icon: 'warning',
            showCancelButton: false,
            didOpen: (popup) => {
                countdownInterval = window.setInterval(() => {
                    const counter = popup.querySelector('b');
                    if (counter !== null) {
                        counter.textContent = formatTimeLeft(expiresAt);
                    }
                }, 1000);
            },
            willClose: () => {
                window.clearInterval(countdownInterval);
            },
        });
    };

    const impersonateUser = (user_data: ImpersonateCallbackResponse) => {
        localStorage.setItem('impersonation_token', user_data.access_token);
        startImpersonation(user_data.access_token);
        localStorage.removeItem('last_activity_at');
        deleteClosedMaintenanceNotificationId();
        accountPermissions.value.permissionList = [];
    };

    const setUser = (_user:LoggedInUserData) => {
        user.value = new LoggedInUser(_user);
        if(_user.accountPermissions !== undefined) {
            accountPermissions.value = new AccountPermissions(_user.accountPermissions);
        }

        const principalType = impersonating.value ? 'impersonated' : 'user';

        Sentry.setUser({id: String(user.value.id)});
        Sentry.setTag('principal_type', principalType);
        Sentry.setAttributes({
            'principal_type': principalType,
            'company.id': String(user.value.company_id),
        });
    };

    const localLogout = async () => {
        clearImpersonationTimers();
        tokenExpiresAt = 0;
        token.value = "";
        impersonating.value = false;
        isLoggedIn.value = false;
        user.value = null;
        accountPermissions.value.permissionList = [];
        localStorage.clear();
        deleteClosedMaintenanceNotificationId();
        deleteClosedSiteNotificationIds();
        await clearKeyPair();
        clearNonce();
        Sentry.setUser(null);
        Sentry.setTag('principal_type', undefined);
        Sentry.getIsolationScope().removeAttribute('principal_type');
        Sentry.getIsolationScope().removeAttribute('company.id');
        window.location.href = router.resolve({name: 'login'}).href;
    };

    const logoutUser = async () => {
        sendSessionMessage({ type: 'logout' });
        try {
            await api.axiosApi.get(`${import.meta.env.VITE_BASE_URL}/api/user/logout`);
        } catch (e) {
            console.log('logout call failed', e);
        }
        await localLogout();
    };

    return {
        token,
        isLoggedIn,
        user,
        initApp,
        loginUser,
        impersonateUser,
        setUser,
        logoutUser,
        localLogout,
        refreshAccessToken,
        ensureFreshToken,
        accountPermissions,
        maintenanceInfo,
        hiddenMaintenanceNotificationId,
        closeMaintenanceNotificationId,
        getClosedMaintenanceNotificationId,
        deleteClosedMaintenanceNotificationId,
        hiddenClientSiteNotificationIds,
        closeSiteNotificationId,
        getClosedSiteNotificationIds,
        deleteClosedSiteNotificationIds,
    };
});
